import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import { api } from '../../services/services/APIs';
import { useDashboard } from '../../context/DashboardContext';
import { useAuth } from '../../context/AuthContext';
import { getCached, setCache, cacheKey } from '../../utils/dashboardCache';
import { AiOutlineReload, AiOutlineBell } from 'react-icons/ai';
import './Dashboard.css';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Forzar colores globales para leyendas en modo oscuro
ChartJS.defaults.plugins.legend.labels.color = '#ffffff';
ChartJS.defaults.color = '#f8fafc';

const formatMoney = (n) => {
  if (n == null || n === '' || typeof n !== 'number') return '$0.00';
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
  return `$${formatted}`;
};

const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const todayStr = () => formatDateLocal(new Date());
const currentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const firstDay = `${year}-${month}-01`;
  return { fechaInicio: firstDay, fechaFin: todayStr() };
};

const daysInMonth = (date) => {
  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  return new Date(year, monthIndex + 1, 0).getDate();
};

/** Días incluidos en el rango del filtro (1 = un solo día). */
const diasEnRangoConsulta = (filtro) => {
  if (!filtro?.fechaInicio || !filtro?.fechaFin) return 1;
  const ini = new Date(`${filtro.fechaInicio}T00:00:00`);
  const fin = new Date(`${filtro.fechaFin}T00:00:00`);
  if (Number.isNaN(ini.getTime()) || Number.isNaN(fin.getTime())) return 1;
  return Math.max(1, Math.round((fin - ini) / 86400000) + 1);
};

const META_DIARIA_NO_APLICA = '-------';

const calcPrevRange = (filtro) => {
  if (filtro.fechaInicio && filtro.fechaFin) {
    const ini = new Date(filtro.fechaInicio + 'T00:00:00');
    const fin = new Date(filtro.fechaFin + 'T00:00:00');
    const days = Math.round((fin - ini) / 86400000) + 1;
    const prevFin = new Date(ini.getTime() - 86400000);
    const prevIni = new Date(prevFin.getTime() - (days - 1) * 86400000);
    return {
      fechaInicio: formatDateLocal(prevIni),
      fechaFin: formatDateLocal(prevFin),
    };
  }
  if (filtro.fecha) {
    const d = new Date(filtro.fecha + 'T00:00:00');
    const prev = new Date(d.getTime() - 86400000);
    return { fecha: formatDateLocal(prev) };
  }
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return { fecha: formatDateLocal(d) };
};

const formatDateLabel = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
};

const previousComparisonLabel = (filtro) => {
  const prev = calcPrevRange(filtro || {});

  if (filtro?.fechaInicio && filtro?.fechaFin) {
    const ini = new Date(`${filtro.fechaInicio}T00:00:00`);
    const fin = new Date(`${filtro.fechaFin}T00:00:00`);
    const days = Math.round((fin - ini) / 86400000) + 1;

    if (days === 1) {
      return `Dia anterior (${formatDateLabel(prev.fechaInicio || prev.fecha)})`;
    }
    if (days === 7) {
      return `Semana anterior (${formatDateLabel(prev.fechaInicio)} - ${formatDateLabel(prev.fechaFin)})`;
    }
    return `Rango anterior (${formatDateLabel(prev.fechaInicio)} - ${formatDateLabel(prev.fechaFin)})`;
  }

  if (filtro?.fecha) {
    return `Dia anterior (${formatDateLabel(prev.fecha)})`;
  }

  return `Ayer (${formatDateLabel(prev.fecha)})`;
};

const ventasDelDiaTotales = (data) => {
  if (data == null) return null;
  const d = data.resumen ?? data;
  const consumo = Number(d.ventasEnConsumoOPiso) || 0;
  const saldadas = Number(d.ventasSaldadas) || 0;
  const cortesias = Number(d.cortesias) || 0;
  const descuentos = Number(d.descuentos) || 0;
  const explicit = Number(d.totalVentasDia);
  if (Number.isFinite(explicit)) {
    return { total: explicit, consumo, saldadas, cortesias, descuentos };
  }
  const fromSum = consumo + saldadas + cortesias + descuentos;
  const fromKpi = Number(d.ventasDia);
  const total = fromSum > 0 ? fromSum : (Number.isFinite(fromKpi) ? fromKpi : 0);
  return { total, consumo, saldadas, cortesias, descuentos };
};

const normalizeFormaPago = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();

/** Configuración y etiquetas de pagos del día (dashboard/completo → ventasDelDia.pagos). */
export const pagosDelDia = {
  tipos: {
    efectivo: 'Efectivo',
    tarjeta: 'Tarjeta',
    cxc: 'CxC',
    monedero: 'Monedero',
    cortesia: 'Cortesía',
    otros: 'Otros',
  },
  colores: {
    efectivo: 'rgba(52, 152, 219, 0.7)', // Blue (como en tu imagen para Efectivo)
    tarjeta: 'rgba(46, 204, 113, 0.7)',  // Green (como en tu imagen para Tarjeta)
    cxc: 'rgba(231, 76, 60, 0.7)',    // Red
    monedero: 'rgba(155, 89, 182, 0.7)', // Purple
    cortesia: 'rgba(149, 165, 166, 0.7)', // Grey
    otros: 'rgba(241, 194, 50, 0.7)',    // Yellow/Orange
  },
  bordes: {
    efectivo: '#3498db',
    tarjeta: '#2ecc71',
    cxc: '#e74c3c',
    monedero: '#9b59b6',
    cortesia: '#95a5a6',
    otros: '#f1c232',
  },
  orden: ['tarjeta', 'efectivo', 'cxc', 'monedero', 'cortesia', 'otros'],
};

/** Agrupa formaPago del API en una de las claves de pagosDelDia.tipos. */
const mapFormaPagoATipo = (formaPago, esCortesia) => {
  if (esCortesia) return 'cortesia';
  const s = normalizeFormaPago(formaPago);
  if (!s) return 'otros';
  if (s.includes('CORTESIA') || s.includes('CORTESÍA')) return 'cortesia';
  if (
    s.includes('TARJETA') ||
    s.includes('VISA') ||
    s.includes('MASTERCARD') ||
    s.includes('AMEX') ||
    s === 'DEBITO' ||
    s.includes('DEBITO')
  ) {
    return 'tarjeta';
  }
  if (s.includes('EFECTIVO') || s === 'CASH') return 'efectivo';
  if (
    s.includes('CXC') ||
    s.includes('C.X.C') ||
    s.includes('CUENTA POR COBRAR') ||
    s.includes('POR COBRAR') ||
    s.includes('CREDITO CLIENTE')
  ) {
    return 'cxc';
  }
  if (s.includes('MONEDERO') || s.includes('WALLET') || s.includes('PUNTOS')) return 'monedero';
  if (s.includes('TRANSFER') || s.includes('SPEI') || s.includes('CHEQUE')) return 'otros';
  return 'otros';
};

const userSucursalId = (user) => {
  if (!user || typeof user !== 'object') return '';
  const direct = [
    user.sucursalId,
    user.idSucursal,
    user.sucursal_id,
    user.idsucursal,
    user.branchId,
  ];
  for (const v of direct) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  const nested = user.sucursal;
  if (nested && typeof nested === 'object') {
    const nestedVals = [nested.sucursalId, nested.id, nested.idSucursal, nested.sucursal_id];
    for (const v of nestedVals) {
      const s = String(v ?? '').trim();
      if (s) return s;
    }
  }
  return '';
};

const userEmpresaId = (user) => {
  if (!user || typeof user !== 'object') return '';
  const direct = [
    user.empresaId,
    user.idEmpresa,
    user.empresa_id,
    user.idempresa,
    user.companyId,
  ];
  for (const v of direct) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  const nested = user.empresa;
  if (nested && typeof nested === 'object') {
    const nestedVals = [nested.empresaId, nested.id, nested.idEmpresa, nested.empresa_id];
    for (const v of nestedVals) {
      const s = String(v ?? '').trim();
      if (s) return s;
    }
  }
  return '';
};

/**
 * Query para GET /dashboard/completo: rango (fechaInicio, fechaFin) + usuario (alcance por usuario).
 * El backend también acepta nombreUsuario; enviamos usuario alineado con la spec.
 */
const paramsDashboardCompleto = (filtro, user) => {
  const out = { ...(filtro || {}) };
  if (!user || typeof user !== 'object') return out;
  const usuario = String(user.usuario ?? user.nombreUsuario ?? user.username ?? '').trim();
  if (usuario) out.usuario = usuario;
  const personaId = String(user.personaId ?? user.persona_id ?? user.idPersona ?? '').trim();
  if (personaId) out.personaId = personaId;
  return out;
};

/** Extrae sucursales del payload de dashboard/completo. */
const extractSucursales = (payload) => {
  const arr = payload?.resumen?.sucursales;
  return Array.isArray(arr) ? arr : [];
};

/**
 * Fusiona dos listas de sucursales sin perder ninguna.
 * Si una sucursal aparece en ambas, prioriza la de `primary` (periodo actual).
 */
const mergeSucursalesLists = (primary, secondary) => {
  const map = new Map();
  for (const s of secondary) {
    if (s?.sucursalId) map.set(s.sucursalId, s);
  }
  for (const s of primary) {
    if (s?.sucursalId) map.set(s.sucursalId, s);
  }
  return [...map.values()];
};

const asUuidOrEmpty = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s
  )
    ? s
    : '';
};

const pickNumber = (obj, keys = []) => {
  if (!obj || typeof obj !== 'object') return 0;
  for (const key of keys) {
    const n = Number(obj?.[key]);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const cancelacionesFromPayload = (cancelacionesObj) => {
  const rows = Array.isArray(cancelacionesObj?.data)
    ? cancelacionesObj.data
    : Array.isArray(cancelacionesObj?.rows)
      ? cancelacionesObj.rows
      : [];
  const montoDesdeRows = rows.reduce((sum, row) => {
    const importe = Number(row?.importe);
    if (Number.isFinite(importe) && importe !== 0) return sum + Math.abs(importe);
    const precio = Number(row?.precio);
    const cantidad = Number(row?.cantidad);
    if (Number.isFinite(precio) && Number.isFinite(cantidad) && cantidad !== 0) {
      return sum + Math.abs(precio * cantidad);
    }
    if (Number.isFinite(precio) && precio !== 0) return sum + Math.abs(precio);
    return sum;
  }, 0);
  const montoDirecto = pickNumber(cancelacionesObj, [
    'importe',
    'importeTotal',
    'monto',
    'totalImporte',
  ]);
  const totalDirecto = Number(cancelacionesObj?.total);
  return {
    monto: montoDirecto > 0 ? montoDirecto : montoDesdeRows,
    cuentas: Number.isFinite(totalDirecto) ? totalDirecto : rows.length,
  };
};

const cancelacionesKpi = (obj) => ({
  monto: pickNumber(obj, [
    'cancelacionesDia',
    'montoCancelacionesDia',
    'ventasCanceladasDia',
    'cancelacionesImporteDia',
  ]),
  cuentas: pickNumber(obj, [
    'mesasCanceladas',
    'cuentasCanceladas',
    'cancelacionesTotales',
    'cancelaciones',
    'canceladas',
  ]),
});

const cancelacionesFromSucursalResumen = (sucursalObj) => {
  const detalle = sucursalObj?.cancelacionesDetalle;
  if (detalle && typeof detalle === 'object') return cancelacionesFromPayload(detalle);
  return { monto: 0, cuentas: Number(sucursalObj?.cancelaciones) || 0 };
};

const metaActualFromPayload = (metaObj) => {
  const direct = pickNumber(metaObj, [
    'actual',
    'actualMes',
    'actualMensual',
    'montoActual',
    'ventasActual',
    'ventasMes',
  ]);
  if (direct > 0) return direct;
  const datos = Array.isArray(metaObj?.datos)
    ? metaObj.datos.map((v) => Number(v)).filter((v) => Number.isFinite(v))
    : [];
  if (datos.length === 0) return 0;
  // Si viene serie, normalmente el ultimo punto representa el acumulado actual.
  return Number(datos[datos.length - 1]) || 0;
};

const parsePuntoEquilibrioValor = (raw) => {
  if (raw == null) return { valor: '', porcentaje: '' };
  if (typeof raw === 'object') {
    return {
      valor: String(raw?.valor ?? '').trim(),
      porcentaje: String(raw?.porcentaje ?? '').trim(),
    };
  }
  const text = String(raw).trim();
  if (!text) return { valor: '', porcentaje: '' };
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') {
      return {
        valor: String(parsed?.valor ?? '').trim(),
        porcentaje: String(parsed?.porcentaje ?? '').trim(),
      };
    }
  } catch {
    // ignore parse error and treat value as plain number string
  }
  return { valor: text, porcentaje: '' };
};



const readJsonSafe = async (res) => {
  if (!res) return null;
  const contentType = String(res.headers?.get('content-type') || '').toLowerCase();
  if (!contentType.includes('application/json')) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
};

/**
 * Respuesta oficial de dashboard/completo → data.pagos { labels, datos, colores?, porcentajes? }.
 * Prioridad para la gráfica "Pagos del día".
 */
const pagosChartDesdeApi = (pagos) => {
  if (!pagos || !Array.isArray(pagos.labels) || !Array.isArray(pagos.datos)) return null;
  if (pagos.labels.length === 0 || pagos.datos.length === 0) return null;
  const n = Math.min(pagos.labels.length, pagos.datos.length);
  const montos = Object.fromEntries(pagosDelDia.orden.map((k) => [k, 0]));
  for (let i = 0; i < n; i += 1) {
    const tipo = mapFormaPagoATipo(pagos.labels[i], false);
    const importe = Number(pagos.datos[i]) || 0;
    if (montos[tipo] !== undefined) montos[tipo] += importe;
    else montos.otros += importe;
  }
  const datos = pagos.datos.slice(0, n).map((d) => Number(d) || 0);
  return {
    labels: pagos.labels.slice(0, n),
    datos,
    ...(Array.isArray(pagos.colores) && pagos.colores.length >= n ? { colores: pagos.colores.slice(0, n) } : {}),
    ...(Array.isArray(pagos.porcentajes) && pagos.porcentajes.length >= n
      ? { porcentajes: pagos.porcentajes.slice(0, n) }
      : {}),
    pagosDelDia: {
      ...pagosDelDia,
      montos,
    },
  };
};

const pagosDesdeVentasDelDia = (ventasDelDia) => {
  const pagos = ventasDelDia?.pagos;
  if (!Array.isArray(pagos) || pagos.length === 0) return null;
  const montos = Object.fromEntries(pagosDelDia.orden.map((k) => [k, 0]));
  pagos.forEach((row) => {
    const tipo = mapFormaPagoATipo(row?.formaPago, row?.esCortesia);
    const importe = Number(row?.importe) || 0;
    if (montos[tipo] !== undefined) montos[tipo] += importe;
    else montos.otros += importe;
  });
  const entries = pagosDelDia.orden
    .map((k) => [pagosDelDia.tipos[k], montos[k]])
    .filter(([, value]) => value > 0);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return {
    labels: entries.map(([label]) => label),
    datos: entries.map(([, value]) => value),
    pagosDelDia: {
      ...pagosDelDia,
      montos,
    },
  };
};

/** Labels alineados con dashboard/completo → data.costosVenta y data.costoDeVentas. */
const LABELS_COSTOS_VENTA = [
  'Ventas',
  '- Cor + Desc',
  'Ingresos',
  'Costo',
  'Alimentos',
  'Bebidas',
  'Otros',
];

/**
 * Pagos del día: % = (importe forma de pago ÷ total de todos los pagos) × 100.
 * Los importes vienen de las cuentas del turno/rango consultado.
 */
const pctPagosDelDia = (valor, datos = []) => {
  const total = datos.reduce((sum, v) => sum + (Number(v) || 0), 0);
  if (total <= 0) return '0.0';
  return ((Number(valor) / total) * 100).toFixed(1);
};

const normalizeCostoLabel = (label) =>
  String(label ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/^-\s*/, '')
    .trim();

/** Paleta fija por concepto — barra y leyenda usan el mismo color. */
const COSTOS_VENTA_PALETTE = [
  '#00e5ff', // Ventas
  '#ff3d71', // Cor + Desc
  '#00ff88', // Ingresos
  '#ffaa00', // Costo
  '#a855f7', // Alimentos
  '#3b82f6', // Bebidas
  '#f472b6', // Otros
];

const costoVentaPaletteIndex = (label) => {
  const norm = normalizeCostoLabel(label);
  if (norm.includes('venta') && !norm.includes('ingreso') && !norm.includes('costo')) return 0;
  if (norm.includes('cor') && norm.includes('desc')) return 1;
  if (norm.includes('ingreso')) return 2;
  if (norm.includes('costo')) return 3;
  if (norm.includes('alimento')) return 4;
  if (norm.includes('bebida')) return 5;
  if (norm.includes('otro')) return 6;
  return -1;
};

const getCostoVentaColor = (label, idx = 0, coloresFromApi) => {
  const byLabel = costoVentaPaletteIndex(label);
  const paletteIdx = byLabel >= 0 ? byLabel : idx;
  if (Array.isArray(coloresFromApi) && coloresFromApi[paletteIdx]) {
    return coloresFromApi[paletteIdx];
  }
  return COSTOS_VENTA_PALETTE[paletteIdx % COSTOS_VENTA_PALETTE.length];
};

const attachCostosVentaColores = (chartData) => {
  if (!chartData?.labels?.length) return chartData;
  if (Array.isArray(chartData.colores) && chartData.colores.length >= chartData.labels.length) {
    return chartData;
  }
  return {
    ...chartData,
    colores: chartData.labels.map((label, idx) =>
      getCostoVentaColor(label, idx, chartData.colores),
    ),
  };
};

/** Obtiene montos base Ventas e Ingresos desde la serie de costos (sin depender del orden visual). */
const getCostosVentasBases = (chartData) => {
  const labels = chartData?.labels ?? [];
  const datos = chartData?.datos ?? [];
  let ventas = 0;
  let ingresos = 0;
  labels.forEach((label, i) => {
    const norm = normalizeCostoLabel(label);
    const val = Number(datos[i]) || 0;
    if (norm.includes('venta') && !norm.includes('ingreso') && !norm.includes('costo')) ventas = val;
    if (norm.includes('ingreso')) ingresos = val;
  });
  if (!ventas && datos.length > 0) ventas = Number(datos[0]) || 0;
  if (!ingresos && datos.length > 2) ingresos = Number(datos[2]) || 0;
  return { ventas, ingresos };
};

/**
 * Costo de ventas: bases distintas por barra (solo cuentas saldadas en backend).
 * Ventas = 100%; Cor+Desc, Ingresos, Ali/Beb/Otros ÷ ventas; Costo ÷ ingresos.
 */
const pctCostoDeVentas = (label, valor, chartData) => {
  const { ventas, ingresos } = getCostosVentasBases(chartData);
  const val = Number(valor) || 0;
  const norm = normalizeCostoLabel(label);

  if (norm.includes('venta') && !norm.includes('ingreso') && !norm.includes('costo')) {
    return ventas > 0 ? '100.0' : '0.0';
  }
  if (norm.includes('cor') && norm.includes('desc')) {
    return ventas > 0 ? ((val / ventas) * 100).toFixed(1) : '0.0';
  }
  if (norm.includes('ingreso')) {
    return ventas > 0 ? ((val / ventas) * 100).toFixed(1) : '0.0';
  }
  if (norm.includes('costo')) {
    return ingresos > 0 ? ((val / ingresos) * 100).toFixed(1) : '0.0';
  }
  return ventas > 0 ? ((val / ventas) * 100).toFixed(1) : '0.0';
};

const buildCostosPorcentajes = (labels, datos) =>
  labels.map((label, idx) => `${pctCostoDeVentas(label, datos[idx], { labels, datos })}%`);

const pctCostoDeVentasDisplay = (label, idx, chartData) =>
  pctCostoDeVentas(label, chartData?.datos?.[idx], chartData);

/** Filas de leyenda de costos (siempre todas; el filtrado es solo para la gráfica). */
const costosLegendRows = (chartData, { sortOrder = '' } = {}) => {
  if (!chartData?.labels?.length) return [];
  let rows = chartData.labels.map((label, idx) => ({
    label,
    valor: Number(chartData.datos?.[idx]) || 0,
    idx,
    color: getCostoVentaColor(label, idx, chartData.colores),
  }));
  if (sortOrder === 'asc' || sortOrder === 'desc') {
    rows.sort((a, b) => (sortOrder === 'asc' ? a.valor - b.valor : b.valor - a.valor));
  }
  return rows;
};

const getCostoBarLabelFromContext = (context) =>
  context.chart?.data?.labels?.[context.dataIndex] ?? context.label ?? '';

/** Fallback: cortesías + descuentos desde importes si no viene el consolidado en costoDeVentas. */
const importeCorYDesc = (importes) => {
  if (!importes || typeof importes !== 'object') return 0;
  const yc = Number(importes.importeDescuentosYCortesias);
  if (Number.isFinite(yc)) return yc;
  return (Number(importes.importeDescuentos) || 0) + (Number(importes.importeCortesias) || 0);
};

/**
 * Columna "- Cor + Desc": debe usar `costoDeVentas.cortesias_descuentos` (cortesías + descuentos, un solo valor).
 * Si el API no lo envía, se calcula desde `importes`.
 */
const montoCorYDesc = (importes, costoDeVentas) => {
  const c = costoDeVentas;
  if (c && typeof c === 'object') {
    const raw = c.cortesias_descuentos;
    if (raw != null && raw !== '') {
      const v = Number(raw);
      if (Number.isFinite(v)) return v;
    }
  }
  return importeCorYDesc(importes ?? {});
};

/** Si no viene costosVenta completo, arma la serie desde costoDeVentas + importes. */
const costosVentaDesdeObjetoBackend = (data) => {
  const c = data?.costoDeVentas ?? data?.costosDeVentas;
  if (!c || typeof c !== 'object') return null;
  const imp = data?.importes ?? data?.costosVenta?.importes ?? {};
  const datos = [
    Number(c.ventas) || 0,
    montoCorYDesc(imp, c),
    Number(c.ingresos) || 0,
    Number(c.costos) || 0,
    Number(c.alimentos) || 0,
    Number(c.bebidas) || 0,
    Number(c.otros) || 0,
  ];
  if (datos.every((v) => v === 0)) return null;
  const labels = LABELS_COSTOS_VENTA;
  return attachCostosVentaColores({
    labels,
    datos,
    porcentajes: buildCostosPorcentajes(labels, datos),
  });
};

/**
 * Gráfica "Costos de venta": prioriza data.costosVenta; si no hay serie, usa costoDeVentas.
 */
const costosVentaChartDesdeApi = (data) => {
  const cv = data?.costosVenta;
  if (cv?.labels?.length && cv?.datos?.length) {
    const n = Math.min(cv.labels.length, cv.datos.length);
    if (n > 0) {
      return attachCostosVentaColores({
        labels: cv.labels.slice(0, n),
        datos: cv.datos.slice(0, n).map((d) => Number(d) || 0),
        ...(Array.isArray(cv.colores) && cv.colores.length >= n ? { colores: cv.colores.slice(0, n) } : {}),
        ...(Array.isArray(cv.porcentajes) && cv.porcentajes.length >= n
          ? { porcentajes: cv.porcentajes.slice(0, n) }
          : {}),
      });
    }
  }
  return costosVentaDesdeObjetoBackend(data);
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sucursales, setSucursales, selectedSucursalId, selectSucursal } = useDashboard();
  const [fechaInicioInput, setFechaInicioInput] = useState(todayStr);
  const [fechaFinInput, setFechaFinInput] = useState(todayStr);
  const [filtroFechas, setFiltroFechas] = useState(() => {
    const hoy = todayStr();
    return { fechaInicio: hoy, fechaFin: hoy };
  });
  const [consultarVersion, setConsultarVersion] = useState(0);
  const [rawData, setRawData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [metaVentaMesActual, setMetaVentaMesActual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sortPagos, setSortPagos] = useState('desc');
  const [sortCostos, setSortCostos] = useState('desc');
  const [expandedSucursalId, setExpandedSucursalId] = useState(null);
  const [sucursalGraficasById, setSucursalGraficasById] = useState({});
  const [loadingSucursalGraficas, setLoadingSucursalGraficas] = useState({});
  const [sucursalesDesactivadas, setSucursalesDesactivadas] = useState({});
  const [pagosDesactivados, setPagosDesactivados] = useState({});
  const [costosDesactivados, setCostosDesactivados] = useState({});
  const [cancelacionesDesactivadas, setCancelacionesDesactivadas] = useState({});
  const hasLoadedOnceRef = useRef(false);
const [peValorConfig, setPeValorConfig] = useState('');
  const [pePorcentajeConfig, setPePorcentajeConfig] = useState('');
  const [showPeModal, setShowPeModal] = useState(false);
  const [metaMensualConfig, setMetaMensualConfig] = useState(0);
  const [showMetaMensualModal, setShowMetaMensualModal] = useState(false);
  const [savingMetaMensual, setSavingMetaMensual] = useState(false);
  const [savingPuntoEquilibrio, setSavingPuntoEquilibrio] = useState(false);
  const [metaMensualConfigId, setMetaMensualConfigId] = useState('');
  const [puntoEquilibrioConfigId, setPuntoEquilibrioConfigId] = useState('');
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileRef = useRef(null);
  const scopedSucursalId = asUuidOrEmpty(selectedSucursalId || userSucursalId(user));
  const scopedEmpresaId = asUuidOrEmpty(userEmpresaId(user));

  useEffect(() => {
    if (!profileDropdownOpen) return;
    const handleOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [profileDropdownOpen]);

  useEffect(() => {
    // Al cambiar de sesión/empresa, limpia sucursales del contexto anterior.
    setSucursales([]);
    selectSucursal(null);
  }, [user?.personaId, scopedEmpresaId, setSucursales, selectSucursal]);

  useEffect(() => {
    let cancelled = false;
    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    setError(null);

    const fetchOrCache = async (filtro, forceNetwork = false) => {
      const key = cacheKey(filtro);
      const shouldUseCache = !forceNetwork;
      if (shouldUseCache) {
        const cached = await getCached(key);
        if (cached) return cached;
      }
      const res = await api.dashboard.completo(filtro);
      if (!res.ok) throw new Error(res.statusText);
      const json = await res.json();
      const data = json.success ? (json.data ?? json) : json;
      if (data) await setCache(key, data);
      return data;
    };

    const load = async () => {
      try {
        // Siempre consulta backend al entrar/recargar dashboard.
        // Importante: solo una petición a /dashboard/completo por ciclo de carga.
        const forceNetwork = true;
        const qActual = paramsDashboardCompleto(filtroFechas, user);
        const data = await fetchOrCache(qActual, forceNetwork);
        const metaMesActual = await (async () => {
          const res = await api.dashboard.metaVenta({
            ...currentMonthRange(),
            ...(scopedSucursalId ? { sucursalId: scopedSucursalId } : {}),
          });
          if (!res.ok) return null;
          const json = await readJsonSafe(res);
          return json?.success ? (json.data ?? null) : null;
        })();
        const [cfgMetaMensualRes, cfgPuntoEquilibrioRes] = await Promise.allSettled([
          api.cuentas.metaMensual({
            nombre: 'MetaVentas',
            valor: Number(metaMesActual?.meta) || 0,
            formato: 'number',
            tipoDato: 1,
            ...(scopedEmpresaId ? { empresaId: scopedEmpresaId } : {}),
          }),
          api.cuentas.crearConfiguracionMeta({
            nombre: 'PuntoEquilibrioNuevo',
            valor: JSON.stringify({
              valor: String(Number(data?.puntoEquilibrio?.meta) || 0),
              porcentaje: '0',
            }),
            formato: 'json',
            tipoDato: 1,
            ...(scopedEmpresaId ? { empresaId: scopedEmpresaId } : {}),
          }),
        ]);
        const cfgMetaMensual = await (async () => {
          if (cfgMetaMensualRes.status !== 'fulfilled') return null;
          const res = cfgMetaMensualRes.value;
          if (!res.ok) return null;
          const json = await readJsonSafe(res);
          return json?.success ? (json.data ?? null) : null;
        })();
        const cfgPuntoEquilibrio = await (async () => {
          if (cfgPuntoEquilibrioRes.status !== 'fulfilled') return null;
          const res = cfgPuntoEquilibrioRes.value;
          if (!res.ok) return null;
          const json = await readJsonSafe(res);
          return json?.success ? (json.data ?? null) : null;
        })();
        if (!cancelled) {
          const parsedPe = parsePuntoEquilibrioValor(cfgPuntoEquilibrio?.valor);
          const metaInicial =
            Number(cfgMetaMensual?.valor) ||
            Number(metaMesActual?.meta) ||
            pickNumber(data?.metaVenta, ['meta', 'metaMensual', 'montoMeta', 'objetivo']) ||
            0;
          setRawData(data);
          setPrevData(null);
          setMetaVentaMesActual(metaMesActual);
          setMetaMensualConfig(metaInicial);
          setMetaMensualConfigId(String(cfgMetaMensual?.configuracionId ?? '').trim());
          setPeValorConfig(parsedPe.valor);
          setPePorcentajeConfig(parsedPe.porcentaje);
          setPuntoEquilibrioConfigId(String(cfgPuntoEquilibrio?.configuracionId ?? '').trim());
          setSucursales(extractSucursales(data));
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Error al cargar datos');
      } finally {
        if (!cancelled) {
          hasLoadedOnceRef.current = true;
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filtroFechas, consultarVersion, scopedEmpresaId, scopedSucursalId, setSucursales, user]);

  useEffect(() => {
    const id = setInterval(() => {
      setConsultarVersion((v) => v + 1);
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const aplicarFechas = () => {
    const hoy = todayStr();
    const iniRaw = fechaInicioInput || fechaFinInput || hoy;
    const finRaw = fechaFinInput || fechaInicioInput || hoy;
    const ini = iniRaw <= finRaw ? iniRaw : finRaw;
    const fin = iniRaw <= finRaw ? finRaw : iniRaw;
    setRefreshing(true);
    setFiltroFechas({
      fechaInicio: ini,
      fechaFin: fin,
    });
    setConsultarVersion((v) => v + 1);
  };

  const resumen = rawData?.resumen;
  const isGeneral = !selectedSucursalId;
  const selectedSuc = selectedSucursalId
    ? resumen?.sucursales?.find((s) => s.sucursalId === selectedSucursalId)
    : null;
  const prevResumen = prevData?.resumen;
  const prevSelectedSuc = selectedSucursalId
    ? prevResumen?.sucursales?.find((s) => s.sucursalId === selectedSucursalId)
    : null;
  
  const sucursalesListado = mergeSucursalesLists(
    extractSucursales(rawData),
    mergeSucursalesLists(extractSucursales(prevData), sucursales)
  );
  const ventasDelDiaObj = isGeneral 
    ? rawData?.ventasDelDia 
    : null;
  const prevVentasDelDiaObj = isGeneral 
    ? prevData?.ventasDelDia 
    : null;
  const ventasDias = rawData?.ventasDias;
  const pagos = rawData?.pagos;
  const pagosChart =
    pagosChartDesdeApi(pagos) ?? pagosDesdeVentasDelDia(ventasDelDiaObj) ?? pagos;
  const costosVenta = attachCostosVentaColores(
    costosVentaChartDesdeApi(rawData) ?? rawData?.costosVenta,
  );
  const metaVenta = rawData?.metaVenta;
  const metaVentaActual = metaVentaMesActual ?? metaVenta;
  const metaVentaDia = rawData?.metaVentaDia;
  const puntoEquilibrio = rawData?.puntoEquilibrio;
  const ventasHora = rawData?.ventasHora;
  const gastosDelDia = rawData?.gastosDelDia;
  const prevGastosDelDia = prevData?.gastosDelDia;
  const gastosKpiActual = isGeneral
    ? {
        total: gastosDelDia?.total ?? 0,
        count: gastosDelDia?.count ?? 0,
      }
    : {
        total: selectedSuc?.gastosDia ?? 0,
        count: selectedSuc?.gastosTotales ?? 0,
      };
  const gastosKpiPrev = isGeneral
    ? {
        total: prevGastosDelDia?.total ?? 0,
        count: prevGastosDelDia?.count ?? 0,
      }
    : {
        total: prevSelectedSuc?.gastosDia ?? 0,
        count: prevSelectedSuc?.gastosTotales ?? 0,
      };
  const ventasPorSucursalChart = useMemo(() => {
    if (ventasDias?.labels?.length > 0) return ventasDias;
    if (!Array.isArray(sucursalesListado) || sucursalesListado.length === 0) return null;
    const labels = [];
    const datos = [];
    for (const suc of sucursalesListado) {
      const nombre = String(suc?.nombreSucursal ?? suc?.NombreSucursal ?? '').trim();
      const monto = Number(
        suc?.ventaSaldadaDia ??
          suc?.ventasDia ??
          suc?.ventaDia ??
          suc?.totalVentas ??
          suc?.total ??
          0
      );
      labels.push(nombre || 'Sucursal');
      datos.push(Number.isFinite(monto) ? monto : 0);
    }
    if (labels.length === 0) return null;
    return { labels, datos };
  }, [ventasDias, sucursalesListado]);

  const ventasPorSucursalChartFiltrado = useMemo(() => {
    if (!ventasPorSucursalChart) return null;
    const labels = [];
    const datos = [];
    const colorIndices = [];
    ventasPorSucursalChart.labels.forEach((label, idx) => {
      if (!sucursalesDesactivadas[label]) {
        labels.push(label);
        datos.push(ventasPorSucursalChart.datos[idx]);
        colorIndices.push(idx);
      }
    });
    return labels.length > 0 ? { labels, datos, colorIndices } : null;
  }, [ventasPorSucursalChart, sucursalesDesactivadas]);

  const pagosChartFiltrado = useMemo(() => {
    if (!pagosChart) return null;
    const labels = [];
    const datos = [];
    pagosChart.labels.forEach((label, idx) => {
      if (!pagosDesactivados[label]) {
        labels.push(label);
        datos.push(pagosChart.datos[idx]);
      }
    });
    if (labels.length === 0) return null;
    return {
      labels,
      datos,
      ...(pagosChart.colores ? { colores: pagosChart.colores } : {}),
      ...(pagosChart.pagosDelDia ? { pagosDelDia: pagosChart.pagosDelDia } : {}),
    };
  }, [pagosChart, pagosDesactivados]);

  const costosVentaFiltrado = useMemo(() => {
    if (!costosVenta) return null;
    const labels = [];
    const datos = [];
    const colores = [];
    costosVenta.labels.forEach((label, idx) => {
      if (!costosDesactivados[label]) {
        labels.push(label);
        datos.push(costosVenta.datos[idx]);
        colores.push(getCostoVentaColor(label, idx, costosVenta.colores));
      }
    });
    if (labels.length === 0) return null;
    return { labels, datos, colores };
  }, [costosVenta, costosDesactivados]);

  const kpis = isGeneral
    ? (resumen?.empresa ?? resumen?.sucursales?.[0])
    : selectedSuc;

  const prevKpisGeneral = prevResumen?.empresa ?? prevResumen?.sucursales?.[0];
  const prevKpis = isGeneral ? prevKpisGeneral : prevSelectedSuc;
  const previousLabel = previousComparisonLabel(filtroFechas);
  const ventasDelDiaActual = ventasDelDiaTotales(ventasDelDiaObj);
  const ventasDelDiaPrev = ventasDelDiaTotales(prevVentasDelDiaObj);
  const descuentosMontoActual = Number(rawData?.importes?.importeDescuentos) || 0;
  const descuentosMontoPrev = Number(prevData?.importes?.importeDescuentos) || 0;
  const ventasDiaMontoActual =
    (ventasDelDiaActual?.total ?? (Number(kpis?.ventasDia) || 0)) - descuentosMontoActual;
  const ventasDiaMontoPrev =
    (ventasDelDiaPrev?.total ?? (Number(prevKpis?.ventasDia) || 0)) - descuentosMontoPrev;
  const cortesiasMontoActual = Number(rawData?.importes?.importeCortesias) || 0;
  const cortesiasMontoPrev = Number(prevData?.importes?.importeCortesias) || 0;
  // Cancelaciones: sumar todas las sucursales en vista general, datos de sucursal cuando está seleccionada
  const cancelacionesPayloadActual = cancelacionesFromPayload(rawData?.cancelaciones);
  const cancelacionesPayloadPrev = cancelacionesFromPayload(prevData?.cancelaciones);
  
  // Calcular total de cancelaciones sumando todas las sucursales
  const calcularCancelacionesTotales = (resumenData) => {
    if (!resumenData?.sucursales || !Array.isArray(resumenData.sucursales)) {
      return { monto: 0, cuentas: 0 };
    }
    let totalMonto = 0;
    let totalCuentas = 0;
    resumenData.sucursales.forEach(suc => {
      const cancelSuc = cancelacionesFromSucursalResumen(suc);
      totalMonto += cancelSuc.monto || 0;
      totalCuentas += cancelSuc.cuentas || 0;
    });
    return { monto: totalMonto, cuentas: totalCuentas };
  };
  
  const cancelacionesActual = isGeneral 
    ? (() => {
        const totales = calcularCancelacionesTotales(resumen);
        const kpiData = cancelacionesKpi(kpis);
        const payloadData = cancelacionesPayloadActual;
        return {
          monto: totales.monto || (kpiData.monto ?? payloadData.monto),
          cuentas: totales.cuentas || (kpiData.cuentas ?? payloadData.cuentas),
        };
      })()
    : (() => {
        const sucData = cancelacionesFromSucursalResumen(selectedSuc);
        return {
          monto: sucData?.monto ?? 0,
          cuentas: sucData?.cuentas ?? 0,
        };
      })();
  
  const cancelacionesPrev = isGeneral 
    ? (() => {
        const totales = calcularCancelacionesTotales(prevResumen);
        const kpiData = cancelacionesKpi(prevKpis);
        const payloadData = cancelacionesPayloadPrev;
        return {
          monto: totales.monto || (kpiData.monto ?? payloadData.monto),
          cuentas: totales.cuentas || (kpiData.cuentas ?? payloadData.cuentas),
        };
      })()
    : (() => {
        const sucData = cancelacionesFromSucursalResumen(prevSelectedSuc);
        return {
          monto: sucData?.monto ?? 0,
          cuentas: sucData?.cuentas ?? 0,
        };
      })();

  const today = new Date();
  const diasMesActual = daysInMonth(today);
  const metaMensualObjetivo =
    metaMensualConfig > 0
      ? metaMensualConfig
      : pickNumber(metaVentaActual, ['meta', 'metaMensual', 'montoMeta', 'objetivo']) || 0;
  /** Meta diaria fija (mensual / días del mes). No depende del rango ni se suma por días consultados. */
  const metaDiariaObjetivo = (() => {
    if (metaMensualObjetivo > 0 && diasMesActual > 0) {
      return metaMensualObjetivo / diasMesActual;
    }
    const metaApi = Number(metaVentaDia?.meta) || 0;
    return metaApi;
  })();
  const esConsultaUnDia = diasEnRangoConsulta(filtroFechas) === 1;
  const ventasMetaDiaria = esConsultaUnDia
    ? (ventasDelDiaActual?.total ?? (Number(kpis?.ventasDia) || 0))
    : null;
  const metaMensualActual = metaActualFromPayload(metaVentaActual);
  const apiPeMeta = Number(puntoEquilibrio?.meta) || 0;
  const peGastosMensuales = Number(peValorConfig) || 0;
  const peCostoVentaPct = Number(pePorcentajeConfig) || 0;
  const peUtilidadPct = 1 - peCostoVentaPct / 100;
  const peMensual =
    peGastosMensuales > 0 && peCostoVentaPct >= 0 && peCostoVentaPct < 100 && peUtilidadPct > 0
      ? peGastosMensuales / peUtilidadPct
      : 0;
  const peDiario =
    peMensual > 0 && diasMesActual > 0 ? peMensual / diasMesActual : apiPeMeta;

  const cmpBadge = (current, previous) => {
    const cur = Number(current) || 0;
    const prev = Number(previous) || 0;
    if (prev === 0 && cur === 0) return null;
    if (prev === 0) return <span className="kpi-cmp kpi-cmp--up">▲ nuevo</span>;
    const diff = ((cur - prev) / Math.abs(prev)) * 100;
    const pct = Math.abs(diff).toFixed(0);
    if (diff > 0) return <span className="kpi-cmp kpi-cmp--up">▲ {pct}%</span>;
    if (diff < 0) return <span className="kpi-cmp kpi-cmp--down">▼ {pct}%</span>;
    return <span className="kpi-cmp kpi-cmp--neutral">= 0%</span>;
  };

  const darkGrid = { color: 'rgba(255,255,255,0.06)' };
  const darkTicks = { color: '#8b9dc3' };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#f8fafc', padding: 16, usePointStyle: true, pointStyleWidth: 8 },
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleColor: '#f1f5f9',
        bodyColor: '#cbd5e1',
        borderColor: '#334155',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: (context) => {
            const dataset = context.dataset;
            const value = context.raw;
            const total = dataset.data.reduce((acc, val) => acc + (Number(val) || 0), 0);
            const label = dataset.label || '';
            const formatted = typeof value === 'number' ? formatMoney(value) : value;
            if (total > 0 && typeof value === 'number') {
              const pct = ((value / total) * 100).toFixed(1);
              return `${label ? label + ': ' : ''}${formatted} (${pct}%)`;
            }
            return `${label ? label + ': ' : ''}${formatted}`;
          },
        },
      },
    },
    scales: {
      x: { grid: darkGrid, ticks: darkTicks },
      y: { grid: darkGrid, ticks: darkTicks },
    },
  };

  const pagosChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        display: false,
        position: 'top',
        onClick: (e, legendItem, legend) => {
          const index = legendItem.index;
          const chart = legend.chart;
          chart.toggleDataVisibility(index);
          chart.update();
        },
        labels: {
          color: '#ffffff',
          padding: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 11, weight: 'bold' },
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const dataset = data.datasets[0];
              const total = dataset.data.reduce((acc, val) => acc + (Number(val) || 0), 0);
              return data.labels.map((label, i) => {
                const value = Number(dataset.data[i]) || 0;
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return {
                  text: `${label}: ${pct}%`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor[i],
                  lineWidth: 1,
                  pointStyle: 'circle',
                  fontColor: '#ffffff',
                  color: '#ffffff',
                  hidden: isNaN(dataset.data[i]) || chart.getDatasetMeta(0).data[i].hidden,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
    },
  };

  const buildCostosChartOptions = (sourceChart) => ({
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        display: false,
        position: 'top',
        onClick: (e, legendItem, legend) => {
          const index = legendItem.index;
          const chart = legend.chart;
          chart.toggleDataVisibility(index);
          chart.update();
        },
        labels: {
          color: '#ffffff',
          padding: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 10, weight: 'bold' },
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const dataset = data.datasets[0];
              const chartRef = {
                labels: sourceChart?.labels ?? data.labels,
                datos: (sourceChart?.datos ?? dataset.data).map((d) => Number(d) || 0),
              };
              return data.labels.map((label, i) => {
                const value = Number(dataset.data[i]) || 0;
                const pct = pctCostoDeVentas(label, value, chartRef);
                return {
                  text: `${label}: ${pct}%`,
                  fillStyle: getCostoVentaColor(label, i, sourceChart?.colores),
                  strokeStyle: 'transparent',
                  lineWidth: 1,
                  pointStyle: 'circle',
                  fontColor: '#ffffff',
                  color: '#ffffff',
                  hidden: isNaN(dataset.data[i]) || chart.getDatasetMeta(0).data[i].hidden,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleColor: '#f1f5f9',
        bodyColor: '#cbd5e1',
        borderColor: '#334155',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: (context) => {
            const barLabel = getCostoBarLabelFromContext(context);
            const value = context.raw;
            const formatted = typeof value === 'number' ? formatMoney(value) : value;
            const chartRef = {
              labels: sourceChart?.labels ?? context.chart.data.labels ?? [],
              datos: (sourceChart?.datos ?? context.chart.data.datasets[0]?.data ?? []).map(
                (d) => Number(d) || 0,
              ),
            };
            const pct = pctCostoDeVentas(barLabel, value, chartRef);
            const norm = normalizeCostoLabel(barLabel);
            const baseHint =
              norm.includes('costo') && !norm.includes('venta') ? ' s/ ingresos' : ' s/ ventas';
            const isVentasRef =
              norm.includes('venta') && !norm.includes('ingreso') && !norm.includes('costo');
            return `${barLabel ? `${barLabel}: ` : ''}${formatted} (${pct}%${isVentasRef ? '' : baseHint})`;
          },
        },
      },
    },
  });

  const costosChartOptions = buildCostosChartOptions(costosVenta);

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#8b9dc3', padding: 16, usePointStyle: true, pointStyleWidth: 8 },
      },
      tooltip: chartOptions.plugins.tooltip,
    },
  };

  const ventasPorSucursalOptions = {
    ...pieOptions,
    plugins: {
      ...pieOptions.plugins,
      legend: { display: false },
    },
  };

  const ventaPorCajaOptions = {
    ...pieOptions,
    plugins: {
      ...pieOptions.plugins,
      legend: { display: false },
    },
  };

  const VIVID = [
    '#00e5ff', '#ff3d71', '#ffaa00', '#00ff88',
    '#a855f7', '#ff6b6b', '#36d399', '#3b82f6',
    '#f472b6', '#fbbf24', '#06d6a0', '#818cf8',
    '#fb923c', '#22d3ee', '#e879f9', '#34d399',
  ];
  const vividColor = (i) => VIVID[i % VIVID.length];

  const pieData = (data) => {
    if (!data?.labels?.length || !data?.datos?.length) return null;
    // Validar que haya al menos un dato mayor a 0
    const hasData = data.datos.some(d => Number(d) > 0);
    if (!hasData) return null;
    return {
      labels: data.labels,
      datasets: [
        {
          data: data.datos,
          backgroundColor:
            Array.isArray(data.colorIndices) && data.colorIndices.length === data.labels.length
              ? data.colorIndices.map((idx) => vividColor(idx))
              : data.labels.map((_, i) => vividColor(i)),
          borderWidth: 2,
          borderColor: '#152036',
        },
      ],
    };
  };

  const rankColors = (values) => {
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    return values.map((v) => {
      const t = (v - min) / range;
      if (t >= 0.8) return '#00ff88';
      if (t >= 0.6) return '#00e5ff';
      if (t >= 0.4) return '#00b4d8';
      if (t >= 0.2) return '#0077b6';
      return '#ff3d71';
    });
  };



  const barDataCostosSorted = (data, sortOrder) => {
    if (!data?.labels?.length || !data?.datos?.length) return null;
    const hasData = data.datos.some((d) => Number(d) > 0);
    if (!hasData) return null;

    let pairs = data.labels.map((label, i) => ({
      label,
      dato: Number(data.datos[i]) || 0,
      idx: i,
      color: getCostoVentaColor(label, i, data.colores),
    }));

    if (sortOrder === 'asc' || sortOrder === 'desc') {
      pairs.sort((a, b) => (sortOrder === 'asc' ? a.dato - b.dato : b.dato - a.dato));
    }

    return {
      labels: pairs.map((p) => p.label),
      datasets: [
        {
          label: 'Monto',
          data: pairs.map((p) => p.dato),
          backgroundColor: pairs.map((p) => p.color),
          borderColor: pairs.map(() => 'transparent'),
          borderWidth: 0,
          borderRadius: 6,
        },
      ],
    };
  };

  const barDataSorted = (data, sortOrder, useRank = false) => {
    if (!data?.labels?.length || !data?.datos?.length) return null;
    // Validar que haya al menos un dato mayor a 0
    const hasData = data.datos.some(d => Number(d) > 0);
    if (!hasData) return null;
    
    let pairs = data.labels.map((l, i) => {
      const labelNorm = normalizeFormaPago(l);
      let key = Object.keys(pagosDelDia.tipos).find(k => normalizeFormaPago(pagosDelDia.tipos[k]) === labelNorm);
      
      // Fallback manual por si acaso
      if (!key) {
        if (labelNorm.includes('TARJETA')) key = 'tarjeta';
        else if (labelNorm.includes('EFECTIVO')) key = 'efectivo';
        else if (labelNorm.includes('CXC')) key = 'cxc';
        else if (labelNorm.includes('CORTESIA')) key = 'cortesia';
      }

      const colorFromData =
        Array.isArray(data.colores) && data.colores[i] ? data.colores[i] : null;
      return {
        label: l, 
        dato: Number(data.datos[i]) || 0,
        idx: i,
        key,
        color: key ? pagosDelDia.colores[key] : (colorFromData || vividColor(i)),
        border: key ? pagosDelDia.bordes[key] : 'transparent',
      };
    });

    if (sortOrder === 'asc' || sortOrder === 'desc') {
      pairs.sort((a, b) => (sortOrder === 'asc' ? a.dato - b.dato : b.dato - a.dato));
    }
    
    const valores = pairs.map((p) => p.dato);
    const hasPagoPalette = pairs.some((p) => Boolean(p.key));
    return {
      labels: pairs.map((p) => p.label),
      datasets: [
        {
          label: 'Monto',
          data: valores,
          backgroundColor: useRank ? rankColors(valores) : pairs.map((p) => p.color),
          borderColor: pairs.map((p) => p.border),
          borderWidth: hasPagoPalette ? 2 : 0,
          borderRadius: 6,
        },
      ],
    };
  };

  const lineData = (data) => {
    if (!data?.labels?.length) return null;
    return {
      labels: data.labels,
      datasets: [
        {
          label: 'Ventas',
          data: data.datos,
          fill: true,
          borderColor: '#00e5ff',
          backgroundColor: 'rgba(0, 229, 255, 0.1)',
          tension: 0.4,
          pointBackgroundColor: '#00e5ff',
          pointBorderColor: '#152036',
          pointBorderWidth: 2,
          borderWidth: 2.5,
        },
      ],
    };
  };

  const loadSucursalGraficas = async (sucursalId) => {
    if (!sucursalId || loadingSucursalGraficas[sucursalId]) return;
    setLoadingSucursalGraficas((prev) => ({ ...prev, [sucursalId]: true }));
    try {
      const res = await api.dashboard.sucursalGraficas({ sucursalId, ...filtroFechas });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const data = json?.success ? (json.data ?? null) : null;
      setSucursalGraficasById((prev) => ({ ...prev, [sucursalId]: data }));
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las graficas de sucursal');
    } finally {
      setLoadingSucursalGraficas((prev) => ({ ...prev, [sucursalId]: false }));
    }
  };

  const upsertConfiguracionMeta = async (
    nombre,
    valor,
    configuracionId = '',
    tipo = 'puntoEquilibrio'
  ) => {
    const id = String(configuracionId || '').trim();
    if (tipo === 'metaMensual') {
      const target = Number(valor) || 0;
      const payload = {
        nombre: 'MetaVentas',
        valor: target,
        formato: 'number',
        tipoDato: 1,
        ...(scopedEmpresaId ? { empresaId: scopedEmpresaId } : {}),
      };
      if (id) {
        const res = await api.cuentas.actualizarMetaMensual(id, payload);
        if (res.ok) {
          const json = await readJsonSafe(res);
          return json?.data ?? null;
        }
        if (res.status !== 404) throw new Error(`HTTP ${res.status}`);
      }
      const ensureRes = await api.cuentas.metaMensual(payload);
      if (!ensureRes.ok) throw new Error(`HTTP ${ensureRes.status}`);
      const ensureJson = await readJsonSafe(ensureRes);
      const ensureData = ensureJson?.data ?? null;
      const ensureId = String(ensureData?.configuracionId ?? '').trim();
      const ensureValor = Number(ensureData?.valor);
      if (ensureId && Number.isFinite(ensureValor) && ensureValor !== target) {
        const patchRes = await api.cuentas.actualizarMetaMensual(ensureId, payload);
        if (!patchRes.ok) throw new Error(`HTTP ${patchRes.status}`);
        const patchJson = await readJsonSafe(patchRes);
        return patchJson?.data ?? ensureData;
      }
      return ensureData;
    }

    const payload = {
      nombre,
      valor: typeof valor === 'string' ? valor : JSON.stringify(valor ?? {}),
      formato: 'json',
      tipoDato: 1,
      ...(scopedEmpresaId ? { empresaId: scopedEmpresaId } : {}),
    };
    let res = null;
    if (id) {
      res = await api.cuentas.actualizarConfiguracionMeta(id, payload);
    }
    if (!res || (!res.ok && res.status === 404)) {
      res = await api.cuentas.crearConfiguracionMeta(payload);
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await readJsonSafe(res);
    return json?.data ?? null;
  };

  const guardarMetaMensual = async () => {
    try {
      setSavingMetaMensual(true);
      const value = Number(metaMensualConfig) || 0;
      const saved = await upsertConfiguracionMeta(
        'MetaVentas',
        value,
        metaMensualConfigId,
        'metaMensual'
      );
      setMetaMensualConfigId(String(saved?.configuracionId ?? metaMensualConfigId ?? '').trim());
      setShowMetaMensualModal(false);
      setConsultarVersion((v) => v + 1);
    } catch (e) {
      setError(e.message || 'No se pudo guardar la meta mensual');
    } finally {
      setSavingMetaMensual(false);
    }
  };

  const guardarPuntoEquilibrio = async () => {
    try {
      setSavingPuntoEquilibrio(true);
      const valor = String(peValorConfig ?? '').trim();
      const porcentaje = String(pePorcentajeConfig ?? '').trim();
      if (!valor || !porcentaje) {
        throw new Error('Captura valor y porcentaje para Punto de equilibrio.');
      }
      const pctNum = Number(porcentaje);
      if (!Number.isFinite(pctNum) || pctNum < 0 || pctNum >= 100) {
        throw new Error('El porcentaje debe ser mayor o igual a 0 y menor a 100.');
      }
      const saved = await upsertConfiguracionMeta(
        'PuntoEquilibrioNuevo',
        { valor, porcentaje },
        puntoEquilibrioConfigId
      );
      const parsed = parsePuntoEquilibrioValor(saved?.valor);
      if (parsed.valor) setPeValorConfig(parsed.valor);
      if (parsed.porcentaje) setPePorcentajeConfig(parsed.porcentaje);
      setPuntoEquilibrioConfigId(
        String(saved?.configuracionId ?? puntoEquilibrioConfigId ?? '').trim()
      );
      setShowPeModal(false);
      setConsultarVersion((v) => v + 1);
    } catch (e) {
      setError(e.message || 'No se pudo guardar el punto de equilibrio');
    } finally {
      setSavingPuntoEquilibrio(false);
    }
  };

  useEffect(() => {
    setSucursalGraficasById({});
    setLoadingSucursalGraficas({});
  }, [filtroFechas]);

  useEffect(() => {
    if (!expandedSucursalId) return;
    loadSucursalGraficas(expandedSucursalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedSucursalId, filtroFechas]);

  // Cargar gráficas cuando se selecciona una sucursal desde el sidebar
  useEffect(() => {
    if (selectedSucursalId && !isGeneral) {
      loadSucursalGraficas(selectedSucursalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSucursalId, filtroFechas]);


  if (loading) return <div className="dashboard-loading">Cargando...</div>;
  if (error) return <div className="dashboard-error">Error: {error}</div>;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-title-box">
          <span className="dashboard-label-prefix">Empresa</span>
          <h1>
            {(user?.nombreEmpresa ||
              user?.nombreCompleto ||
              user?.razonSocial ||
              user?.Empresa?.nombre ||
              user?.Empresa?.razonSocial ||
              user?.Empresa?.RazonSocial ||
              user?.empresa?.nombre ||
              user?.empresa?.razonSocial ||
              user?.nombre_comercial ||
              sucursales?.[0]?.nombreEmpresa ||
              sucursales?.[0]?.NombreEmpresa ||
              resumen?.empresa?.nombre ||
              '').replace(/^Empresa\s+/i, '') || 'Sin Nombre'}
            {selectedSucursalId && ` - ${selectedSuc?.nombreSucursal || 'Sucursal'}`}
          </h1>
        </div>
        <div className="dashboard-header-right">
          <div className="dashboard-filtro-fecha">
            <div className="dashboard-date-pill">
              <span className="dashboard-date-label">Desde:</span>
              <input
                type="date"
                lang="es-MX"
                value={fechaInicioInput}
                onChange={(e) => setFechaInicioInput(e.target.value)}
                className="dashboard-fecha"
              />
            </div>
            <div className="dashboard-date-pill">
              <span className="dashboard-date-label">Hasta:</span>
              <input
                type="date"
                lang="es-MX"
                value={fechaFinInput}
                onChange={(e) => setFechaFinInput(e.target.value)}
                className="dashboard-fecha"
              />
            </div>
            <button
              type="button"
              className="dashboard-btn-consultar"
              onClick={aplicarFechas}
              disabled={loading || refreshing}
            >
              {refreshing || loading ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
          <button type="button" className="dashboard-bell-btn" aria-label="Notificaciones">
            <AiOutlineBell className="dashboard-bell-icon" />
          </button>
          <div className="dashboard-profile-wrapper" ref={profileRef}>
            <div
              className={`dashboard-profile-avatar${profileDropdownOpen ? ' open' : ''}`}
              onClick={() => setProfileDropdownOpen((v) => !v)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setProfileDropdownOpen((v) => !v)}
              aria-label="Menú de perfil"
            >
              {(user?.nombreCompleto || user?.nombreUsuario || 'U').charAt(0).toUpperCase()}
            </div>
            {profileDropdownOpen && (
              <div className="dashboard-profile-dropdown">
                <div className="dashboard-profile-dropdown-empresa">
                  {(
                    user?.nombreEmpresa ||
                    user?.empresa?.razonSocial ||
                    user?.nombre_comercial ||
                    sucursales?.[0]?.nombreEmpresa ||
                    sucursales?.[0]?.NombreEmpresa ||
                    ''
                  ).replace(/^Empresa\s+/i, '') || 'Sin empresa'}
                </div>
                <button
                  type="button"
                  className="dashboard-profile-dropdown-item"
                  onClick={() => {
                    setProfileDropdownOpen(false);
                    navigate('/perfil');
                  }}
                >
                  Mi Perfil
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {refreshing && !loading && (
        <div className="dashboard-loading-overlay">
          <div className="dashboard-loading-modal">
            <AiOutlineReload className="dashboard-spinner-icon" aria-hidden="true" />
            <div>
              <strong>Cargando datos</strong>
              <p>Actualizando el contenido del dashboard, espera un momento...</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal configuración punto de equilibrio */}
      {isGeneral && showPeModal && (
        <div className="dashboard-modal-backdrop" onClick={() => setShowPeModal(false)}>
          <div
            className="dashboard-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="dashboard-modal-header">
              <h2>Configurar punto de equilibrio</h2>
              <button
                type="button"
                className="dashboard-modal-close"
                onClick={() => setShowPeModal(false)}
              >
                ×
              </button>
            </div>
            <div className="dashboard-modal-body">
              <div className="dashboard-pe-config-grid">
                <label>
                  <span>Gastos del mes ($)</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={peValorConfig}
                    onChange={(e) => {
                      setPeValorConfig(e.target.value);
                    }}
                  />
                </label>
                <label>
                  <span>Costo de venta (%)</span>
                  <input
                    type="number"
                    min="0"
                    max="99.99"
                    step="0.01"
                    value={pePorcentajeConfig}
                    onChange={(e) => {
                      setPePorcentajeConfig(e.target.value);
                    }}
                  />
                </label>
              </div>
              <p className="dashboard-modal-help">
                Se guarda como objeto en <strong>valor</strong>: {`{"valor":"...","porcentaje":"..."}`}.
                Luego el sistema calcula el punto de equilibrio mensual (gastos / utilidad) y lo divide
                entre {diasMesActual} días para obtener el valor diario.
              </p>
            </div>
            <div className="dashboard-modal-footer">
              <button
                type="button"
                className="dashboard-btn-consultar"
                onClick={guardarPuntoEquilibrio}
                disabled={savingPuntoEquilibrio}
              >
                {savingPuntoEquilibrio ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isGeneral && showMetaMensualModal && (
        <div className="dashboard-modal-backdrop" onClick={() => setShowMetaMensualModal(false)}>
          <div
            className="dashboard-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="dashboard-modal-header">
              <h2>Configurar meta mensual</h2>
              <button
                type="button"
                className="dashboard-modal-close"
                onClick={() => setShowMetaMensualModal(false)}
              >
                ×
              </button>
            </div>
            <div className="dashboard-modal-body">
              <div className="dashboard-pe-config-grid">
                <label>
                  <span>Meta mensual ($)</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={metaMensualConfig || ''}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const safe = Number.isFinite(value) && value >= 0 ? value : 0;
                      setMetaMensualConfig(safe);
                    }}
                  />
                </label>
              </div>
              <p className="dashboard-modal-help">
                La meta diaria se calcula automáticamente: meta mensual / días del mes ({diasMesActual}).
              </p>
            </div>
            <div className="dashboard-modal-footer">
              <button
                type="button"
                className="dashboard-btn-consultar"
                onClick={guardarMetaMensual}
                disabled={savingMetaMensual}
              >
                {savingMetaMensual ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tarjetas KPI */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-card--positive">
          <h3>Ventas del día</h3>
          <div className="kpi-value-row">
            <p className="kpi-value">{formatMoney(ventasDiaMontoActual)}</p>
            {cmpBadge(ventasDiaMontoActual, ventasDiaMontoPrev)}
          </div>
          <span className="kpi-prev">{previousLabel}: {formatMoney(ventasDiaMontoPrev)}</span>
        </div>
        <div className="kpi-card kpi-card--positive">
          <h3>Venta saldada</h3>
          <div className="kpi-value-row">
            <p className="kpi-value">{formatMoney(kpis?.ventaSaldadaDia)}</p>
            {cmpBadge(kpis?.ventaSaldadaDia, prevKpis?.ventaSaldadaDia)}
          </div>
          <span className={`kpi-sub ${(kpis?.mesasSaldadas ?? 0) > 0 ? 'kpi-sub--up' : ''}`}>
            {(kpis?.mesasSaldadas ?? 0) > 0 && '▲ '}{kpis?.mesasSaldadas ?? 0} cuentas saldadas
          </span>
          {prevKpis && <span className="kpi-prev">{previousLabel}: {formatMoney(prevKpis?.ventaSaldadaDia)}</span>}
        </div>
        <div className={`kpi-card ${(kpis?.ventaAbiertaDia ?? 0) > 0 ? 'kpi-card--negative' : 'kpi-card--neutral'}`}>
          <h3>Venta abierta</h3>
          <div className="kpi-value-row">
            <p className="kpi-value">{formatMoney(kpis?.ventaAbiertaDia)}</p>
            {cmpBadge(kpis?.ventaAbiertaDia, prevKpis?.ventaAbiertaDia)}
          </div>
          <span className="kpi-sub">
            {(kpis?.mesasAbiertas ?? 0) === 0 ? 'Sin cuentas abiertas' : `${kpis.mesasAbiertas} cuentas en atención`}
          </span>
          {prevKpis && <span className="kpi-prev">{previousLabel}: {formatMoney(prevKpis?.ventaAbiertaDia)}</span>}
        </div>
        <div className={`kpi-card ${(gastosKpiActual.total ?? 0) > 0 ? 'kpi-card--negative' : 'kpi-card--neutral'}`}>
          <h3>Gastos del día</h3>
          <div className="kpi-value-row">
            <p className="kpi-value">{formatMoney(gastosKpiActual.total)}</p>
            {cmpBadge(gastosKpiActual.total, gastosKpiPrev.total)}
          </div>
          <span className={`kpi-sub ${(gastosKpiActual.count ?? 0) > 0 ? 'kpi-sub--down' : ''}`}>
            {(gastosKpiActual.count ?? 0) > 0 && '▼ '}{gastosKpiActual.count ?? 0} gastos registrados
          </span>
          {(isGeneral ? prevGastosDelDia : prevSelectedSuc) && (
            <span className="kpi-prev">{previousLabel}: {formatMoney(gastosKpiPrev.total)}</span>
          )}
        </div>
        <div className="kpi-card kpi-card--neutral">
          <h3>Importe de descuentos</h3>
          <div className="kpi-value-row">
            <p className="kpi-value">{formatMoney(descuentosMontoActual)}</p>
            {cmpBadge(descuentosMontoActual, descuentosMontoPrev)}
          </div>
          <span className="kpi-sub">Importe cortesias: {formatMoney(cortesiasMontoActual)}</span>
          <span className="kpi-prev">{previousLabel}: Desc. {formatMoney(descuentosMontoPrev)} · Cort. {formatMoney(cortesiasMontoPrev)}</span>
        </div>
        <div className={`kpi-card ${(cancelacionesActual.monto ?? 0) > 0 ? 'kpi-card--negative' : 'kpi-card--neutral'}`}>
          <h3>Cancelaciones</h3>
          <div className="kpi-value-row">
            <p className="kpi-value">{formatMoney(cancelacionesActual.monto)}</p>
            {cmpBadge(cancelacionesActual.monto, cancelacionesPrev.monto)}
          </div>
          <span className={`kpi-sub ${(cancelacionesActual.cuentas ?? 0) > 0 ? 'kpi-sub--down' : ''}`}>
            {(cancelacionesActual.cuentas ?? 0) > 0
              ? `▼ ${cancelacionesActual.cuentas} cancelaciones`
              : 'Sin cancelaciones'}
          </span>
          {prevKpis && (
            <span className="kpi-prev">
              {previousLabel}: {formatMoney(cancelacionesPrev.monto)} · {cancelacionesPrev.cuentas} cancelaciones
            </span>
          )}
        </div>
      </div>

      {/* Gráficas (solo vista general) */}
      {isGeneral && <div className="charts-grid">
        {/* Ventas por hora - línea */}
        {ventasHora?.labels?.length > 0 && (
          <div className="chart-card chart-wide">
            <h3>Ventas por hora</h3>
            <div className="chart-container">
              <Line data={lineData(ventasHora)} options={chartOptions} />
            </div>
          </div>
        )}

        {/* Ventas por sucursal - dona */}
        {ventasPorSucursalChart?.labels?.length > 0 && (
          <div className="chart-card">
            <h3>Ventas por sucursal</h3>
            <div
              className="chart-container chart-pie"
              style={{ minHeight: `${Math.max(170, Math.min(230, ventasPorSucursalChart.labels.length * 8))}px` }}
            >
              {ventasPorSucursalChartFiltrado && pieData(ventasPorSucursalChartFiltrado) ? (
                <Doughnut
                  data={pieData(ventasPorSucursalChartFiltrado)}
                  options={ventasPorSucursalOptions}
                />
              ) : (
                <Doughnut
                  data={{
                    labels: ventasPorSucursalChart.labels,
                    datasets: [{
                      data: ventasPorSucursalChart.datos,
                      backgroundColor: ventasPorSucursalChart.labels.map(() => '#ccc'),
                      borderWidth: 2,
                      borderColor: '#152036',
                    }]
                  }}
                  options={ventasPorSucursalOptions}
                />
              )}
            </div>
            <div className="ventas-sucursal-legend">
              {ventasPorSucursalChart.labels.map((label, idx) => {
                const valor = Number(ventasPorSucursalChart.datos?.[idx] || 0);
                const total = ventasPorSucursalChart.datos.reduce((sum, v) => sum + Number(v || 0), 0);
                const porcentaje = total > 0 ? ((valor / total) * 100).toFixed(1) : 0;
                const desactivada = !!sucursalesDesactivadas[label];
                return (
                  <div
                    key={`${label}-${idx}`}
                    className={`ventas-sucursal-legend-item ${desactivada ? 'ventas-sucursal-legend-item--disabled' : ''}`}
                    onClick={() => {
                      setSucursalesDesactivadas((prev) => ({
                        ...prev,
                        [label]: prev[label] ? undefined : true
                      }));
                    }}
                  >
                    <span
                      className="ventas-sucursal-legend-dot"
                      style={{ backgroundColor: desactivada ? '#ccc' : vividColor(idx) }}
                    />
                    <span className="ventas-sucursal-legend-label">{label}</span>
                    <span className="ventas-sucursal-legend-value">{formatMoney(valor)} ({porcentaje}%)</span>
                    {desactivada && <span className="ventas-sucursal-legend-x">×</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pagos del día - barras */}
        {pagosChart?.labels?.length > 0 && (
          <div className="chart-card">
            <div className="chart-card-header">
              <h3>Pagos del día</h3>
              <div className="chart-sort-buttons">
                <button
                  type="button"
                  className={sortPagos === 'asc' ? 'active' : ''}
                  onClick={() => setSortPagos(sortPagos === 'asc' ? '' : 'asc')}
                  title="Ascendente"
                >
                  ↑ Asc
                </button>
                <button
                  type="button"
                  className={sortPagos === 'desc' ? 'active' : ''}
                  onClick={() => setSortPagos(sortPagos === 'desc' ? '' : 'desc')}
                  title="Descendente"
                >
                  ↓ Desc
                </button>
              </div>
            </div>
            <div className="chart-container chart-bar">
              {barDataSorted(pagosChartFiltrado, sortPagos, false) ? (
                <Bar data={barDataSorted(pagosChartFiltrado, sortPagos, false)} options={pagosChartOptions} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#8b9dc3' }}>
                  No hay datos para mostrar
                </div>
              )}
            </div>
            <div className="pagos-legend-simple">
              {pagosChart.labels
                .map((label, idx) => ({
                  label,
                  valor: Number(pagosChart.datos?.[idx] || 0),
                  idx
                }))
                .sort((a, b) => b.valor - a.valor)
                .map(({ label, valor, idx }) => {
                  const porcentaje = pctPagosDelDia(valor, pagosChart.datos);
                  const labelNorm = normalizeFormaPago(label);
                  let key = Object.keys(pagosDelDia.tipos).find(k => normalizeFormaPago(pagosDelDia.tipos[k]) === labelNorm);
                  if (!key) {
                    if (labelNorm.includes('TARJETA')) key = 'tarjeta';
                    else if (labelNorm.includes('EFECTIVO')) key = 'efectivo';
                    else if (labelNorm.includes('CXC')) key = 'cxc';
                    else if (labelNorm.includes('CORTESIA')) key = 'cortesia';
                  }
                  const color = key ? pagosDelDia.colores[key] : vividColor(idx);
                  const isDisabled = !!pagosDesactivados[label];
                  return (
                    <button
                      key={label}
                      type="button"
                      className={`pagos-legend-btn ${isDisabled ? 'pagos-legend-btn--disabled' : ''}`}
                      onClick={() => {
                        setPagosDesactivados((prev) => ({
                          ...prev,
                          [label]: isDisabled ? false : true
                        }));
                      }}
                    >
                      <span className="pagos-legend-dot" style={{ backgroundColor: isDisabled ? '#ccc' : color }}></span>
                      <span className="pagos-legend-label">{label}</span>
                      <span className="pagos-legend-amount">{formatMoney(valor)}</span>
                      <span className="pagos-legend-value">({porcentaje}%)</span>
                      {isDisabled && <span className="pagos-legend-x">×</span>}
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {/* Cancelaciones por sucursal - dona */}
        {rawData?.cancelacionesPorSucursal?.length > 0 && (() => {
          const cancelacionesData = rawData.cancelacionesPorSucursal;
          // Filtrar solo sucursales con cancelaciones
          const sucursalesConCancelaciones = cancelacionesData.filter(c => Number(c.total) > 0);
          
          if (sucursalesConCancelaciones.length === 0) return null;
          
          // Datos filtrados (sin las desactivadas)
          const cancelacionesFiltradas = sucursalesConCancelaciones.filter(
            c => !cancelacionesDesactivadas[c.nombreSucursal]
          );
          
          const labelsFiltrados = cancelacionesFiltradas.map(c => c.nombreSucursal || 'Sin nombre');
          const datosFiltrados = cancelacionesFiltradas.map(c => Number(c.total) || 0);
          
          return (
            <div className="chart-card">
              <h3>Cancelaciones por sucursal</h3>
              <div
                className="chart-container chart-pie"
                style={{ minHeight: `${Math.max(170, Math.min(230, sucursalesConCancelaciones.length * 8))}px` }}
              >
                {cancelacionesFiltradas.length > 0 && pieData({ labels: labelsFiltrados, datos: datosFiltrados }) ? (
                  <Doughnut
                    data={pieData({ labels: labelsFiltrados, datos: datosFiltrados })}
                    options={ventasPorSucursalOptions}
                  />
                ) : (
                  <Doughnut
                    data={{
                      labels: sucursalesConCancelaciones.map(c => c.nombreSucursal),
                      datasets: [{
                        data: sucursalesConCancelaciones.map(c => Number(c.total)),
                        backgroundColor: sucursalesConCancelaciones.map(() => '#ccc'),
                        borderWidth: 2,
                        borderColor: '#152036',
                      }]
                    }}
                    options={ventasPorSucursalOptions}
                  />
                )}
              </div>
              <div className="ventas-sucursal-legend">
                {sucursalesConCancelaciones.map((cancelacion, idx) => {
                  const valor = Number(cancelacion.total) || 0;
                  const total = sucursalesConCancelaciones.reduce((sum, c) => sum + Number(c.total || 0), 0);
                  const porcentaje = total > 0 ? ((valor / total) * 100).toFixed(1) : 0;
                  const desactivada = !!cancelacionesDesactivadas[cancelacion.nombreSucursal];
                  return (
                    <div
                      key={`${cancelacion.sucursalId}-${idx}`}
                      className={`ventas-sucursal-legend-item ${desactivada ? 'ventas-sucursal-legend-item--disabled' : ''}`}
                      onClick={() => {
                        setCancelacionesDesactivadas((prev) => ({
                          ...prev,
                          [cancelacion.nombreSucursal]: prev[cancelacion.nombreSucursal] ? undefined : true
                        }));
                      }}
                    >
                      <span
                        className="ventas-sucursal-legend-dot"
                        style={{ backgroundColor: desactivada ? '#ccc' : vividColor(idx) }}
                      />
                      <span className="ventas-sucursal-legend-label">{cancelacion.nombreSucursal}</span>
                      <span className="ventas-sucursal-legend-value">({porcentaje}%)</span>
                      {desactivada && <span className="ventas-sucursal-legend-x">×</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Costos de venta - barras */}
        {costosVenta?.labels?.length > 0 && (
          <div className="chart-card">
            <div className="chart-card-header">
              <h3>Costos de venta</h3>
              <div className="chart-sort-buttons">
                <button
                  type="button"
                  className={sortCostos === 'asc' ? 'active' : ''}
                  onClick={() => setSortCostos(sortCostos === 'asc' ? '' : 'asc')}
                  title="Ascendente"
                >
                  ↑ Asc
                </button>
                <button
                  type="button"
                  className={sortCostos === 'desc' ? 'active' : ''}
                  onClick={() => setSortCostos(sortCostos === 'desc' ? '' : 'desc')}
                  title="Descendente"
                >
                  ↓ Desc
                </button>
              </div>
            </div>
            <div className="chart-container chart-bar">
              {(() => {
                const chartData = barDataCostosSorted(costosVentaFiltrado, sortCostos);
                if (!chartData) {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#8b9dc3' }}>
                      No hay datos para mostrar
                    </div>
                  );
                }
                return <Bar data={chartData} options={costosChartOptions} />;
              })()}
            </div>
            <div className="pagos-legend-simple">
              {costosLegendRows(costosVenta, { sortOrder: sortCostos }).map(
                ({ label, valor, idx, color }) => {
                const porcentaje = pctCostoDeVentasDisplay(label, idx, costosVenta);
                const desactivado = !!costosDesactivados[label];
                const dotColor = color || getCostoVentaColor(label, idx, costosVenta?.colores);
                return (
                  <button
                    key={label}
                    type="button"
                    className={`pagos-legend-btn ${desactivado ? 'pagos-legend-btn--disabled' : ''}`}
                    onClick={() => {
                      setCostosDesactivados((prev) => ({
                        ...prev,
                        [label]: desactivado ? false : true
                      }));
                    }}
                  >
                    <span className="pagos-legend-dot" style={{ backgroundColor: desactivado ? '#ccc' : dotColor }}></span>
                    <span className="pagos-legend-label">{label}</span>
                    <span className="pagos-legend-amount">{formatMoney(valor)}</span>
                    <span className="pagos-legend-value">({porcentaje}%)</span>
                    {desactivado && <span className="pagos-legend-x">×</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Meta de venta */}
        {(metaMensualConfig > 0 || metaVentaActual?.meta != null || metaVentaActual?.actual != null) && (() => {
          const meta = metaMensualObjetivo;
          const actual = metaMensualActual;
          const pct = meta > 0 ? Math.min(Math.round((actual / meta) * 100), 999) : 0;
          const isAbove = actual >= meta;
          return (
            <div className="meta-card">
              <div className="meta-card-header">
                <h3 className="meta-card-title">Meta mensual</h3>
                {isGeneral && (
                  <button
                    type="button"
                    className="meta-card-config-btn"
                    onClick={() => {
                      setMetaMensualConfig(metaMensualObjetivo);
                      setShowMetaMensualModal(true);
                    }}
                  >
                    Configurar meta
                  </button>
                )}
              </div>
              <p className="meta-card-value">{formatMoney(actual)}</p>
              <div className="meta-progress-bar">
                <div className={`meta-progress-fill ${isAbove ? 'meta-progress--up' : 'meta-progress--down'}`}
                     style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
              <div className="meta-card-row">
                <span className="meta-card-label">Meta <strong>{formatMoney(meta)}</strong></span>
                <span className={`meta-card-pct ${isAbove ? 'meta-pct--up' : 'meta-pct--down'}`}>
                  {isAbove ? '▲' : '▼'} {pct}%
                </span>
              </div>
              <div className="meta-card-row">
                <span className="meta-card-label">Actual <strong>{formatMoney(actual)}</strong></span>
              </div>
            </div>
          );
        })()}

        {/* Meta venta diaria — la meta es siempre de un día; no se multiplica por el rango. */}
        {(metaMensualConfig > 0 || metaVentaDia?.meta != null || metaVentaDia?.actual != null) && (() => {
          const meta = metaDiariaObjetivo;
          const actual = ventasMetaDiaria ?? 0;
          const mostrarComparacion = esConsultaUnDia;
          const pct =
            mostrarComparacion && meta > 0 ? Math.min(Math.round((actual / meta) * 100), 999) : null;
          const isAbove = mostrarComparacion && actual >= meta;
          return (
            <div className="meta-card">
              <h3 className="meta-card-title">Meta diaria</h3>
              <p className="meta-card-value">
                {mostrarComparacion ? formatMoney(actual) : formatMoney(meta)}
              </p>
              {mostrarComparacion && (
                <div className="meta-progress-bar">
                  <div
                    className={`meta-progress-fill ${isAbove ? 'meta-progress--up' : 'meta-progress--down'}`}
                    style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
                  />
                </div>
              )}
              <div className="meta-card-row">
                <span className="meta-card-label">
                  Meta <strong>{formatMoney(meta)}</strong>
                </span>
                <span
                  className={`meta-card-pct ${
                    mostrarComparacion ? (isAbove ? 'meta-pct--up' : 'meta-pct--down') : 'meta-pct--neutral'
                  }`}
                >
                  {mostrarComparacion ? `${isAbove ? '▲' : '▼'} ${pct}%` : META_DIARIA_NO_APLICA}
                </span>
              </div>
              <div className="meta-card-row">
                <span className="meta-card-label">
                  Actual{' '}
                  <strong>{mostrarComparacion ? formatMoney(actual) : META_DIARIA_NO_APLICA}</strong>
                </span>
              </div>
            </div>
          );
        })()}

        {/* Punto de equilibrio */}
        {(peDiario > 0 || puntoEquilibrio?.actual != null) && (() => {
          const metaDiaria = peDiario;
          const actual = Number(kpis?.ventasDia) || 0;
          const mostrarMetaDiaria = esConsultaUnDia;
          const pct =
            mostrarMetaDiaria && metaDiaria > 0
              ? Math.min(Math.round((actual / metaDiaria) * 100), 999)
              : null;
          const isAbove = mostrarMetaDiaria && actual >= metaDiaria;
          const mostrarMensualYDiario = peMensual > 0 && metaDiaria > 0;
          return (
            <div className="meta-card">
              <div className="meta-card-header">
                <h3 className="meta-card-title">Punto de equilibrio</h3>
                {isGeneral && (
                  <button
                    type="button"
                    className="meta-card-config-btn"
                    onClick={() => setShowPeModal(true)}
                  >
                    Configurar datos
                  </button>
                )}
              </div>
              {mostrarMensualYDiario ? (
                <div className="meta-card-pe-values">
                  <div className="meta-card-pe-block">
                    <span className="meta-card-pe-label">
                      Mensual (gastos / utilidad)
                    </span>
                    <p className="meta-card-pe-value">{formatMoney(peMensual)}</p>
                  </div>
                  <div className="meta-card-pe-block meta-card-pe-block--primary">
                    <span className="meta-card-pe-label">
                      Punto de equilibrio diario
                    </span>
                    <p className="meta-card-pe-value meta-card-pe-value--emphasis">
                      {formatMoney(metaDiaria)}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="meta-card-value">{formatMoney(metaDiaria)}</p>
              )}
              {mostrarMetaDiaria && (
                <div className="meta-progress-bar">
                  <div
                    className={`meta-progress-fill ${
                      isAbove ? 'meta-progress--up' : 'meta-progress--down'
                    }`}
                    style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
                  />
                </div>
              )}
              <div className="meta-card-row">
                <span className="meta-card-label">
                  Punto de equilibrio diario{' '}
                  <strong>{formatMoney(metaDiaria)}</strong>
                </span>
                <span
                  className={`meta-card-pct ${
                    mostrarMetaDiaria ? (isAbove ? 'meta-pct--up' : 'meta-pct--down') : 'meta-pct--neutral'
                  }`}
                >
                  {mostrarMetaDiaria ? `${isAbove ? '▲' : '▼'} ${pct}%` : META_DIARIA_NO_APLICA}
                </span>
              </div>
              <div className="meta-card-row">
                <span className="meta-card-label">
                  Ventas{mostrarMetaDiaria ? ' del día' : ' del periodo'}{' '}
                  <strong>{formatMoney(actual)}</strong>
                </span>
              </div>
            </div>
          );
        })()}
      </div>}

      {/* Gráficas de sucursal seleccionada */}
      {!isGeneral && selectedSucursalId && (() => {
        const sucGraficas = sucursalGraficasById[selectedSucursalId];
        const sucLoading = loadingSucursalGraficas[selectedSucursalId];
        
        if (sucLoading) {
          return <div className="dashboard-loading">Cargando gráficas de sucursal...</div>;
        }
        
        if (!sucGraficas) return null;
        
        return (
          <div className="charts-grid">
            {/* Ventas por hora */}
            {sucGraficas.ventasHora?.labels?.length > 0 && (
              <div className="chart-card chart-wide">
                <h3>Ventas por hora</h3>
                <div className="chart-container">
                  <Line data={lineData(sucGraficas.ventasHora)} options={chartOptions} />
                </div>
              </div>
            )}
            
            {/* Pagos del día */}
            {sucGraficas.pagos?.labels?.length > 0 && (
              <div className="chart-card">
                <h3>Pagos del día</h3>
                <div className="chart-container chart-bar">
                  {(() => {
                    const chartData = barDataSorted(sucGraficas.pagos, 'desc', false);
                    if (!chartData) {
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#8b9dc3' }}>
                          No hay datos para mostrar
                        </div>
                      );
                    }
                    return <Bar data={chartData} options={pagosChartOptions} />;
                  })()}
                </div>
                <div className="pagos-legend-simple">
                  {sucGraficas.pagos.labels
                    .map((label, idx) => ({
                      label,
                      valor: Number(sucGraficas.pagos.datos?.[idx] || 0),
                      idx
                    }))
                    .sort((a, b) => b.valor - a.valor)
                    .map(({ label, valor, idx }) => {
                      const porcentaje = pctPagosDelDia(valor, sucGraficas.pagos.datos);
                      const labelNorm = normalizeFormaPago(label);
                      let key = Object.keys(pagosDelDia.tipos).find(
                        (k) => normalizeFormaPago(pagosDelDia.tipos[k]) === labelNorm
                      );
                      if (!key) {
                        if (labelNorm.includes('TARJETA')) key = 'tarjeta';
                        else if (labelNorm.includes('EFECTIVO')) key = 'efectivo';
                        else if (labelNorm.includes('CXC')) key = 'cxc';
                        else if (labelNorm.includes('CORTESIA')) key = 'cortesia';
                      }
                      const color = key ? pagosDelDia.colores[key] : vividColor(idx);
                      return (
                        <div key={label} className="pagos-legend-item">
                          <span className="pagos-legend-dot" style={{ backgroundColor: color }}></span>
                          <span className="pagos-legend-label">{label}</span>
                          <span className="pagos-legend-amount">{formatMoney(valor)}</span>
                          <span className="pagos-legend-value">({porcentaje}%)</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            
            {/* Costos de venta */}
            {sucGraficas.costosVenta?.labels?.length > 0 && (
              <div className="chart-card">
                <h3>Costos de venta</h3>
                <div className="chart-container chart-bar">
                  {(() => {
                    const sucCostos = attachCostosVentaColores(sucGraficas.costosVenta);
                    const chartData = barDataCostosSorted(sucCostos, 'desc');
                    if (!chartData) {
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#8b9dc3' }}>
                          No hay datos para mostrar
                        </div>
                      );
                    }
                    return (
                      <Bar
                        data={chartData}
                        options={buildCostosChartOptions(sucCostos)}
                      />
                    );
                  })()}
                </div>
                <div className="pagos-legend-simple">
                  {costosLegendRows(attachCostosVentaColores(sucGraficas.costosVenta), {
                    sortOrder: 'desc',
                  }).map(({ label, valor, color }) => {
                      const originalIdx = sucGraficas.costosVenta.labels.indexOf(label);
                      const porcentaje = pctCostoDeVentasDisplay(
                        label,
                        originalIdx >= 0 ? originalIdx : 0,
                        attachCostosVentaColores(sucGraficas.costosVenta),
                      );
                      const dotColor = color || getCostoVentaColor(label, originalIdx, sucGraficas.costosVenta?.colores);
                      return (
                        <div key={label} className="costos-legend-item">
                          <span className="pagos-legend-dot" style={{ backgroundColor: dotColor }}></span>
                          <span className="pagos-legend-label">{label}</span>
                          <span className="pagos-legend-amount">{formatMoney(valor)}</span>
                          <span className="pagos-legend-value">({porcentaje}%)</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            
            {/* Venta por caja */}
            {Array.isArray(selectedSuc?.ventasPorCaja) && selectedSuc.ventasPorCaja.length > 0 && (
              <div className="chart-card">
                <h3>Venta por caja</h3>
                <div
                  className="chart-container chart-pie"
                  style={{ minHeight: `${Math.max(170, Math.min(230, selectedSuc.ventasPorCaja.length * 8))}px` }}
                >
                  {pieData({
                    labels: selectedSuc.ventasPorCaja.map(c => c.nombreCaja || `Caja ${c.caja}`),
                    datos: selectedSuc.ventasPorCaja.map(c => Number(c.ventaTotal) || 0)
                  }) ? (
                    <Doughnut 
                      data={pieData({
                        labels: selectedSuc.ventasPorCaja.map(c => c.nombreCaja || `Caja ${c.caja}`),
                        datos: selectedSuc.ventasPorCaja.map(c => Number(c.ventaTotal) || 0)
                      })} 
                      options={ventaPorCajaOptions} 
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8b9dc3' }}>
                      No hay datos para mostrar
                    </div>
                  )}
                </div>
                <div className="ventas-sucursal-legend">
                  {selectedSuc.ventasPorCaja.map((caja, idx) => {
                    const valor = Number(caja.ventaTotal) || 0;
                    const total = selectedSuc.ventasPorCaja.reduce((sum, c) => sum + Number(c.ventaTotal || 0), 0);
                    const porcentaje = total > 0 ? ((valor / total) * 100).toFixed(1) : 0;
                    return (
                      <div
                        key={`${caja.caja}-${idx}`}
                        className="ventas-sucursal-legend-item"
                      >
                        <span
                          className="ventas-sucursal-legend-dot"
                          style={{ backgroundColor: vividColor(idx) }}
                        />
                        <span className="ventas-sucursal-legend-label">{caja.nombreCaja || `Caja ${caja.caja}`}</span>
                        <span className="ventas-sucursal-legend-value">{formatMoney(valor)} ({porcentaje}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {isGeneral && sucursalesListado.length > 0 && (
        <div className="sucursales-section">
          <h2 className="sucursales-title">Sucursales</h2>
          
          {sucursalesListado.map((suc) => {
            const prevSuc = prevResumen?.sucursales?.find((s) => s.sucursalId === suc.sucursalId);
            const sucGraficas = sucursalGraficasById[suc.sucursalId];
            const sucLoading = loadingSucursalGraficas[suc.sucursalId];
            const sucCancelDetalle = cancelacionesFromSucursalResumen(suc);
            const prevSucCancelDetalle = cancelacionesFromSucursalResumen(prevSuc);
            const sucCancelKpi = cancelacionesKpi(suc);
            const prevSucCancelKpi = cancelacionesKpi(prevSuc);
            const sucCancelMonto = sucCancelKpi.monto || sucCancelDetalle.monto;
            const sucCancelCuentas = sucCancelKpi.cuentas || sucCancelDetalle.cuentas;
            const prevSucCancelMonto = prevSucCancelKpi.monto || prevSucCancelDetalle.monto;
            const prevSucCancelCuentas = prevSucCancelKpi.cuentas || prevSucCancelDetalle.cuentas;
            return (
              <div key={suc.sucursalId} className="sucursal-block">
                <button
                  type="button"
                  className="sucursal-toggle"
                  onClick={() => {
                    const isOpening = expandedSucursalId !== suc.sucursalId;
                    setExpandedSucursalId((prev) => (prev === suc.sucursalId ? null : suc.sucursalId));
                    if (isOpening && !sucursalGraficasById[suc.sucursalId]) {
                      loadSucursalGraficas(suc.sucursalId);
                    }
                  }}
                >
                  <span className="sucursal-name">{suc.nombreSucursal}</span>
                  <span className={`sucursal-toggle-arrow${expandedSucursalId === suc.sucursalId ? ' open' : ''}`}>›</span>
                </button>
                {expandedSucursalId === suc.sucursalId && (
                  <>
                    <div className="kpi-grid">
                      <div className="kpi-card kpi-card--positive">
                        <h3>Venta saldada</h3>
                        <div className="kpi-value-row">
                          <p className="kpi-value">{formatMoney(suc.ventaSaldadaDia)}</p>
                          {cmpBadge(suc.ventaSaldadaDia, prevSuc?.ventaSaldadaDia)}
                        </div>
                        <span className={`kpi-sub ${(suc.mesasSaldadas ?? 0) > 0 ? 'kpi-sub--up' : ''}`}>
                          {(suc.mesasSaldadas ?? 0) > 0 && '▲ '}{suc.mesasSaldadas ?? 0} cuentas saldadas
                        </span>
                        {prevSuc && <span className="kpi-prev">{previousLabel}: {formatMoney(prevSuc?.ventaSaldadaDia)}</span>}
                      </div>
                      <div className={`kpi-card ${(suc.ventaAbiertaDia ?? 0) > 0 ? 'kpi-card--negative' : 'kpi-card--neutral'}`}>
                        <h3>Venta abierta</h3>
                        <div className="kpi-value-row">
                          <p className="kpi-value">{formatMoney(suc.ventaAbiertaDia)}</p>
                          {cmpBadge(suc.ventaAbiertaDia, prevSuc?.ventaAbiertaDia)}
                        </div>
                        <span className="kpi-sub">
                          {(suc.mesasAbiertas ?? 0) === 0 ? 'Sin cuentas abiertas' : `${suc.mesasAbiertas} cuentas en atención`}
                        </span>
                        {prevSuc && <span className="kpi-prev">{previousLabel}: {formatMoney(prevSuc?.ventaAbiertaDia)}</span>}
                      </div>
                      <div className={`kpi-card ${(suc.gastosDia ?? 0) > 0 ? 'kpi-card--negative' : 'kpi-card--neutral'}`}>
                        <h3>Gastos del día</h3>
                        <div className="kpi-value-row">
                          <p className="kpi-value">{formatMoney(suc.gastosDia)}</p>
                          {cmpBadge(suc.gastosDia, prevSuc?.gastosDia)}
                        </div>
                        <span className={`kpi-sub ${(suc.gastosTotales ?? 0) > 0 ? 'kpi-sub--down' : ''}`}>
                          {(suc.gastosTotales ?? 0) > 0 && '▼ '}{suc.gastosTotales ?? 0} gastos registrados
                        </span>
                        {prevSuc && <span className="kpi-prev">{previousLabel}: {formatMoney(prevSuc?.gastosDia)}</span>}
                      </div>
                      <div className={`kpi-card ${(sucCancelMonto ?? 0) > 0 ? 'kpi-card--negative' : 'kpi-card--neutral'}`}>
                        <h3>Cancelaciones</h3>
                        <div className="kpi-value-row">
                          <p className="kpi-value">{formatMoney(sucCancelMonto)}</p>
                          {cmpBadge(sucCancelMonto, prevSucCancelMonto)}
                        </div>
                        <span className={`kpi-sub ${(sucCancelCuentas ?? 0) > 0 ? 'kpi-sub--down' : ''}`}>
                          {(sucCancelCuentas ?? 0) > 0
                            ? `▼ ${sucCancelCuentas} cancelaciones`
                            : 'Sin cancelaciones'}
                        </span>
                        {prevSuc && (
                          <span className="kpi-prev">
                            {previousLabel}: {formatMoney(prevSucCancelMonto)} · {prevSucCancelCuentas} cancelaciones
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {!!sucGraficas && (
                      <div className="charts-grid sucursal-charts-grid">
                        {sucGraficas.ventasHora?.labels?.length > 0 && (
                          <div className="chart-card chart-wide">
                            <h3>Ventas por hora</h3>
                            <div className="chart-container">
                              <Line data={lineData(sucGraficas.ventasHora)} options={chartOptions} />
                            </div>
                          </div>
                        )}
                        {sucGraficas.pagos?.labels?.length > 0 && (
                          <div className="chart-card">
                            <h3>Pagos del dia</h3>
                            <div className="chart-container chart-bar">
                              {(() => {
                                const chartData = barDataSorted(sucGraficas.pagos, 'desc', false);
                                if (!chartData) {
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#8b9dc3' }}>
                                      No hay datos para mostrar
                                    </div>
                                  );
                                }
                                return <Bar data={chartData} options={pagosChartOptions} />;
                              })()}
                            </div>
                            <div className="pagos-legend-simple">
                              {sucGraficas.pagos.labels
                                .map((label, idx) => ({
                                  label,
                                  valor: Number(sucGraficas.pagos.datos?.[idx] || 0),
                                  idx
                                }))
                                .sort((a, b) => b.valor - a.valor)
                                .map(({ label, valor, idx }) => {
                                  const porcentaje = pctPagosDelDia(valor, sucGraficas.pagos.datos);
                                  const labelNorm = normalizeFormaPago(label);
                                  let key = Object.keys(pagosDelDia.tipos).find(
                                    (k) => normalizeFormaPago(pagosDelDia.tipos[k]) === labelNorm
                                  );
                                  if (!key) {
                                    if (labelNorm.includes('TARJETA')) key = 'tarjeta';
                                    else if (labelNorm.includes('EFECTIVO')) key = 'efectivo';
                                    else if (labelNorm.includes('CXC')) key = 'cxc';
                                    else if (labelNorm.includes('CORTESIA')) key = 'cortesia';
                                  }
                                  const color = key ? pagosDelDia.colores[key] : vividColor(idx);
                                  return (
                                    <div key={label} className="pagos-legend-item">
                                      <span className="pagos-legend-dot" style={{ backgroundColor: color }}></span>
                                      <span className="pagos-legend-label">{label}</span>
                                      <span className="pagos-legend-value">{formatMoney(valor)} ({porcentaje}%)</span>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                         {sucGraficas.costosVenta?.labels?.length > 0 && (
                          <div className="chart-card">
                            <h3>Costos de venta</h3>
                            <div className="chart-container chart-bar">
                              {(() => {
                                const sucCostos = attachCostosVentaColores(sucGraficas.costosVenta);
                                const chartData = barDataCostosSorted(sucCostos, 'desc');
                                if (!chartData) {
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#8b9dc3' }}>
                                      No hay datos para mostrar
                                    </div>
                                  );
                                }
                                return (
                                  <Bar
                                    data={chartData}
                                    options={buildCostosChartOptions(sucCostos)}
                                  />
                                );
                              })()}
                            </div>
                            <div className="costos-legend-simple">
                              {costosLegendRows(attachCostosVentaColores(sucGraficas.costosVenta), {
                                sortOrder: 'desc',
                              }).map(({ label, valor, color }) => {
                                  const originalIdx = sucGraficas.costosVenta.labels.indexOf(label);
                                  const porcentaje = pctCostoDeVentasDisplay(
                                    label,
                                    originalIdx >= 0 ? originalIdx : 0,
                                    attachCostosVentaColores(sucGraficas.costosVenta),
                                  );
                                  const dotColor =
                                    color || getCostoVentaColor(label, originalIdx, sucGraficas.costosVenta?.colores);
                                  return (
                                    <div key={label} className="costos-legend-item">
                                      <span className="costos-legend-dot" style={{ backgroundColor: dotColor }}></span>
                                      <span className="costos-legend-label">{label}</span>
                                      <span className="costos-legend-value">
                                        {formatMoney(valor)} ({porcentaje}%)
                                      </span>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                        {Array.isArray(suc.ventasPorCaja) && suc.ventasPorCaja.length > 0 && (
                          <div className="chart-card">
                            <h3>Venta por caja</h3>
                            <div
                              className="chart-container chart-pie"
                              style={{ minHeight: `${Math.max(170, Math.min(230, suc.ventasPorCaja.length * 8))}px` }}
                            >
                              {pieData({
                                labels: suc.ventasPorCaja.map(c => c.nombreCaja || `Caja ${c.caja}`),
                                datos: suc.ventasPorCaja.map(c => Number(c.ventaTotal) || 0)
                              }) ? (
                                <Doughnut 
                                  data={pieData({
                                    labels: suc.ventasPorCaja.map(c => c.nombreCaja || `Caja ${c.caja}`),
                                    datos: suc.ventasPorCaja.map(c => Number(c.ventaTotal) || 0)
                                  })} 
                                  options={ventaPorCajaOptions} 
                                />
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8b9dc3' }}>
                                  No hay datos para mostrar
                                </div>
                              )}
                            </div>
                            <div className="ventas-sucursal-legend">
                              {suc.ventasPorCaja.map((caja, idx) => {
                                const valor = Number(caja.ventaTotal) || 0;
                                const total = suc.ventasPorCaja.reduce((sum, c) => sum + Number(c.ventaTotal || 0), 0);
                                const porcentaje = total > 0 ? ((valor / total) * 100).toFixed(1) : 0;
                                return (
                                  <div
                                    key={`${caja.caja}-${idx}`}
                                    className="ventas-sucursal-legend-item"
                                  >
                                    <span
                                      className="ventas-sucursal-legend-dot"
                                      style={{ backgroundColor: vividColor(idx) }}
                                    />
                                    <span className="ventas-sucursal-legend-label">{caja.nombreCaja || `Caja ${caja.caja}`}</span>
                                    <span className="ventas-sucursal-legend-value">{formatMoney(valor)} ({porcentaje}%)</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {sucLoading && !sucGraficas && (
                      <div className="dashboard-loading">Cargando graficas de sucursal...</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
